import { Injectable } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { Logger } from './logger.service';
import * as enUS from '../../translations/en-US.json';
import * as frFR from '../../translations/fr-FR.json';
import * as esES from '../../translations/es-ES.json';

const log = new Logger('I18nService');
const languageKey = 'language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param s The string to extract for translation.
 * @return The same string.
 */
export function extract(s: string) {
  return s;
}

interface II18n {
  current?: string;
  default: string;
}

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  defaultLanguage!: string;
  supportedLanguages!: string[];
  public current: string = null;

  private langChangeSubscription!: Subscription;
  private readonly STORAGE_KEY: string = 'ADMIN_DASHBOARD:I18N';

  private normalizeLang(lang?: string | null): string {
    const fallback = this.defaultLanguage || 'es-ES';
    if (!lang) {
      return fallback;
    }
    const normalized = String(lang).trim();
    const key = normalized.toLowerCase();
    const map: Record<string, string> = {
      'es': 'es-ES',
      'es-es': 'es-ES',
      'en': 'en-US',
      'en-us': 'en-US',
      'fr': 'fr-FR',
      'fr-fr': 'fr-FR'
    };
    return map[key] || normalized;
  }

  constructor(private translateService: TranslateService) {
    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('fr-FR', frFR);
    translateService.setTranslation('es-ES', esES);
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */
  /* init(defaultLanguage: string, supportedLanguages: string[]) {
     this.defaultLanguage = defaultLanguage;
     this.supportedLanguages = supportedLanguages;
     this.language = '';
 
     // Warning: this subscription will always be alive for the app's lifetime
     this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
       localStorage.setItem(languageKey, event.lang);
     });
   } */

  public async init(): Promise<void> {
    try {
      const i18n = this.get() || { current: null, default: 'es' };
      const defaultLang = this.normalizeLang(i18n.default || 'es');

      /// Set the default language for translation strings, and the current language.
      this.translateService.setDefaultLang(defaultLang);

      /// Save the language if changed
      this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        this.set(event.lang);
        this.current = event.lang;
      });

      if (!!i18n.current) {
        this.set(i18n.current);
      } else {
        const browserLang = this.translateService.getBrowserLang();
        if (!!browserLang) {
          this.set(browserLang);
        }
      }
    } catch (e) {
      console.error('I18nService.init()::catch', e);
    }
  }

  /**
   * Cleans up language change subscription.
   */
  destroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: string) {
    language =
      language ||
      localStorage.getItem(languageKey) ||
      this.translateService.getBrowserCultureLang();
    let isSupportedLanguage = this.supportedLanguages.includes(language);

    // If no exact match is found, search without the region
    if (language && !isSupportedLanguage) {
      language = language.split('-')[0];
      language =
        this.supportedLanguages.find(supportedLanguage =>
          supportedLanguage.startsWith(language)
        ) || '';
      isSupportedLanguage = Boolean(language);
    }

    // Fallback if language is not supported
    if (!isSupportedLanguage) {
      language = this.defaultLanguage;
    }

    log.debug(`Language set to ${language}`);
    this.translateService.use(language);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    return this.translateService.currentLang;
  }

  public set(current: string): void {
    const normalized = this.normalizeLang(current);
    if (this.translateService.currentLang !== normalized) {
      this.translateService.use(normalized);
      localStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify({ current: normalized, default: this.normalizeLang('es') })
      );
    }

    return;
  }

  public get(): II18n {
    return JSON.parse(localStorage.getItem(this.STORAGE_KEY));
  }
}
